import { Typography } from '@mui/joy';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { AccessPermissions, InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useFormContext } from 'core/states';
import { useJobActions } from 'jobs/hooks';
import { IJob } from 'jobs/types';
import { Level } from 'theme/enums';

import { requirementsSchema } from './schema';

interface IProps {
  job: IJob;
}

interface IForm {
  details: string;
}

const RequirementsForm = ({ job }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();
  const { upsertJob } = useJobActions();
  const { dirtyFields } = useFormContext();

  const isEditable =
    job.access?.editAllowed && hasPermission(AccessPermissions.ACCESS_COMPANY_BOM_DETAILS_EDIT);
  const isDirty = dirtyFields.details;

  const onSubmit = (reset: UseFormReset<IForm>) => async (data: IForm) => {
    const { success } = await upsertJob({
      bom: {
        id: job.id,
        details: data.details,
      },
    });

    if (!success) {
      return;
    }

    reset({
      details: data.details,
    });
  };

  const onClose = (reset: UseFormReset<IForm>) => {
    reset();
  };

  return (
    <Form
      inline
      isOpen
      disabledCancel={!isDirty}
      disabledSubmit={!isDirty}
      notificationPosition={NotificationPosition.FORM}
      onSubmit={onSubmit}
      onClose={onClose}
      submitBtnLabel={t('buttonUpdateRequirements')}
    >
      <Typography level={Level.H4}>{t('titleRequirements')}</Typography>

      <FormField
        name="details"
        type={InputType.TEXTAREA}
        inputProps={{
          disabled: !isEditable,
          minRows: 5,
          maxRows: 20,
        }}
      />
    </Form>
  );
};

export const Requirements = ({ job }: IProps) => {
  const defaultValues = useMemo<IForm>(() => {
    return {
      details: job.details || '',
    };
  }, [job.details]);

  return (
    <FormProvider schema={requirementsSchema} defaultValues={defaultValues} resetDefaultValues>
      <RequirementsForm job={job} />
    </FormProvider>
  );
};
